import { Box, colors, Dropdown, Typography } from "@procore/core-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { EventOrigin } from "apis/procore";
import { useAppDispatch, useAppSelector, useAppStore } from "app/redux-store-interactions";
import { useDispatchHidden, useHidden } from "contexts/hidden";
import { useDispatchHighlighted, useHighlighted } from "contexts/highlighted";
import { useDispatchSelectionBasket } from "contexts/selectionBasket";
import { useFlyToSelected } from "features/flyToSelected/useFlyToSelected";
import {
    selectDefaultVisibility,
    selectMainObject,
    selectMainObjects,
    selectPreIsolationVisibility,
} from "features/render";

import { useIsolationVisibility } from "../../objectTree/hooks/useIsolationVisibility";
import { createBulkActions } from "../../objectTree/utils/bulkActions";

export interface BulkActionsDropdownProps {
    selectedCount: number;
    totalCount: number;
}

export function BulkActionsDropdown({ selectedCount, totalCount }: BulkActionsDropdownProps) {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const store = useAppStore();
    const dispatchHidden = useDispatchHidden();
    const dispatchHighlighted = useDispatchHighlighted();
    const dispatchSelectionBasket = useDispatchSelectionBasket();
    const { flyToSelected } = useFlyToSelected();
    const defaultVisibility = useAppSelector(selectDefaultVisibility);
    const preIsolationVisibility = useAppSelector(selectPreIsolationVisibility);
    const mainObject = useAppSelector(selectMainObject);

    const { idArr: geometryIds } = useHighlighted();
    const mainObjects = useAppSelector(selectMainObjects);
    const { ids: hiddenIdsMap, lastHiddenIds } = useHidden();
    const isolationVisibility = useIsolationVisibility();

    // V1 tree selection never populates mainObjects; fall back to the full geometry
    // set so count, disabled checks, and labels all reflect the actual selection.
    const effectiveSelection = mainObjects.length > 0 ? mainObjects : geometryIds;

    const actions = useMemo(
        () =>
            createBulkActions(effectiveSelection.length, effectiveSelection, {
                t,
                dispatch,
                store,
                dispatchHighlighted,
                dispatchHidden,
                dispatchSelectionBasket,
                defaultVisibility,
                preIsolationVisibility,
                flyToSelected,
                currentMainObject: mainObject,
                hiddenIds: hiddenIdsMap,
                lastHiddenIds,
                isolationVisibility,
                geometryIds,
                origin: EventOrigin.PropertiesPanel,
            }),
        [
            effectiveSelection,
            geometryIds,
            t,
            dispatch,
            store,
            dispatchHighlighted,
            dispatchHidden,
            dispatchSelectionBasket,
            defaultVisibility,
            preIsolationVisibility,
            flyToSelected,
            mainObject,
            hiddenIdsMap,
            lastHiddenIds,
            isolationVisibility,
        ],
    );

    return (
        <Box display="flex" alignItems="center" justifyContent="space-between" data-testid="bulk-actions-dropdown">
            <Dropdown
                label={t("actions")}
                variant="tertiary"
                data-testid="bulk-actions-dropdown-button"
                onSelect={({ item }) => {
                    const action = actions.find((a) => a.id === item);
                    if (action && !action.disabled) {
                        action.onClick();
                    }
                }}
            >
                {actions.map((action) => (
                    <Dropdown.Item
                        key={action.id}
                        item={action.id}
                        disabled={action.disabled}
                        data-testid={`bulk-action-${action.id}`}
                    >
                        {action.label}
                    </Dropdown.Item>
                ))}
            </Dropdown>
            <Typography
                data-testid="bulk-actions-count"
                style={{
                    color: colors.gray15,
                }}
            >
                {t("propertiesOfTotal", { selectedCount, totalCount })}
            </Typography>
        </Box>
    );
}
